import type { NumberDisplayOptions } from '@/utils/numberUtils';
import type { I18nKey } from '@/utils/typings';
import { t } from 'i18next';
import { uniqBy } from 'lodash';
import { NIL } from 'uuid';
import {
  instanceOfTrackItMeasurementParameterAreaDto,
  instanceOfTrackItMeasurementParameterBooleanDto,
  instanceOfTrackItMeasurementParameterDoubleDto,
  instanceOfTrackItMeasurementParameterLongDto,
  instanceOfTrackItMeasurementParameterModalityDto,
  instanceOfTrackItMeasurementParameterFilterWithUserValueDto,
  instanceOfTrackItMeasurementPropertyFilterDto,
  ParameterCombinationDto,
  TrackItMeasurementFilterForCombinationDto,
  TrackItMeasurementParameterAreaDtoTypeEnum,
  TrackItMeasurementParameterBooleanDtoTypeEnum,
  TrackItMeasurementParameterDoubleDtoTypeEnum,
  TrackItMeasurementParameterDto,
  TrackItMeasurementParameterFilterDto,
  TrackItMeasurementParameterFilterWithUserValueDtoTypeEnum,
  TrackItMeasurementParameterLongDtoTypeEnum,
  TrackItMeasurementParameterModalityDtoTypeEnum,
  TrackItMeasurementParameterStringDtoTypeEnum,
  TrackItMeasurementPropertyDto,
  TrackItMeasurementPropertyDtoTypeEnum,
  TrackItMeasurementPropertyFilterDto,
  TrackItMeasurementPropertyFilterDtoTypeEnum,
} from '@/api/generator';

// Stand-in for the six TrackItMeasurementParameterDto $type discriminators.
export type TrackItMeasurementParameterKind =
  | 'Area'
  | 'Boolean'
  | 'Double'
  | 'Long'
  | 'Modality'
  | 'String';

export const trackItMeasurementParameterKinds: TrackItMeasurementParameterKind[] = [
  'Area',
  'Boolean',
  'Double',
  'Long',
  'Modality',
  'String',
];

export function measurementParameterKindAllowsUnit(kind: TrackItMeasurementParameterKind) {
  return kind === 'Area' || kind === 'Double' || kind === 'Long';
}

export function kindOfMeasurementParameter(
  param: TrackItMeasurementParameterDto,
): TrackItMeasurementParameterKind {
  if (instanceOfTrackItMeasurementParameterAreaDto(param)) return 'Area';
  if (instanceOfTrackItMeasurementParameterBooleanDto(param)) return 'Boolean';
  if (instanceOfTrackItMeasurementParameterDoubleDto(param)) return 'Double';
  if (instanceOfTrackItMeasurementParameterLongDto(param)) return 'Long';
  if (instanceOfTrackItMeasurementParameterModalityDto(param)) return 'Modality';
  return 'String';
}

// NumberField only accepts a decimal separator for floating-point values, as for numeric setup
// parameters; only Long values are integers.
export function numberFormatOfMeasurementParameter(
  param: TrackItMeasurementParameterDto,
): NumberDisplayOptions {
  return {
    valueType: kindOfMeasurementParameter(param) === 'Long' ? 'IntegerNumber' : 'FloatingPoint',
  };
}

export function unitOfMeasurementParameter(
  param: TrackItMeasurementParameterDto,
): string | undefined {
  if (instanceOfTrackItMeasurementParameterAreaDto(param)) return param.unit ?? undefined;
  if (instanceOfTrackItMeasurementParameterDoubleDto(param)) return param.unit ?? undefined;
  if (instanceOfTrackItMeasurementParameterLongDto(param)) return param.unit ?? undefined;
  return undefined;
}

export function trackItFilterName(
  entry: TrackItMeasurementFilterForCombinationDto,
): string | undefined {
  if (instanceOfTrackItMeasurementPropertyFilterDto(entry)) {
    return entry.measurementProperty?.name ?? undefined;
  }
  return entry.measurementParameter?.name ?? undefined;
}

// A measurement property's name is a translation key. Translated for display only — the key
// itself stays the property's identity and is what gets saved.
export function translateMeasurementPropertyName(name: string | null | undefined): string {
  return name ? t(name as I18nKey) : '';
}

export function isParameterFilterEntry(
  entry: TrackItMeasurementFilterForCombinationDto,
): entry is TrackItMeasurementParameterFilterDto {
  return !instanceOfTrackItMeasurementPropertyFilterDto(entry);
}

// The props of IdentifierLabel: the name, and "Type | Unit" (just the type without a unit).
export function describeMeasurementParameterParts(param: TrackItMeasurementParameterDto): {
  label: string;
  identifier: string;
} {
  const kind = kindOfMeasurementParameter(param);
  const unit = unitOfMeasurementParameter(param);
  return { label: param.name ?? '', identifier: unit ? `${kind} | ${unit}` : kind };
}

// A property filter has no type, so it shows the bare (translated) name.
export function filterLabelParts(filter: TrackItMeasurementFilterForCombinationDto): {
  label: string;
  identifier: string;
} {
  return isParameterFilterEntry(filter) && filter.measurementParameter
    ? describeMeasurementParameterParts(filter.measurementParameter)
    : { label: translateMeasurementPropertyName(trackItFilterName(filter) ?? ''), identifier: '' };
}

// Value + unit as one string; Area has valueX/valueY, Boolean/Modality/String carry no unit.
export function formatMeasurementParameterValue(param: TrackItMeasurementParameterDto): string {
  if (instanceOfTrackItMeasurementParameterAreaDto(param)) {
    if (param.valueX == null && param.valueY == null) return '';
    return `${param.valueX ?? ''} x ${param.valueY ?? ''} ${param.unit ?? ''}`.trim();
  }
  if (instanceOfTrackItMeasurementParameterBooleanDto(param)) {
    return param.value == null ? '' : String(param.value);
  }
  if (
    instanceOfTrackItMeasurementParameterDoubleDto(param) ||
    instanceOfTrackItMeasurementParameterLongDto(param)
  ) {
    return param.value == null ? '' : `${param.value} ${param.unit ?? ''}`.trim();
  }
  if (instanceOfTrackItMeasurementParameterModalityDto(param)) {
    return param.value ?? '';
  }
  return param.value ?? '';
}

// "Name (Type): value", or "Name: value" for a property; the value is left out while empty.
export function describeTrackItFilterWithValue(
  filter: TrackItMeasurementFilterForCombinationDto,
): string {
  if (!isParameterFilterEntry(filter)) {
    const name = translateMeasurementPropertyName(filter.measurementProperty?.name);
    const value = filter.measurementProperty?.value;
    return value ? `${name}: ${value}` : name;
  }

  const name = trackItFilterName(filter) ?? '';
  const param = filter.measurementParameter;
  const label = param ? `${name} (${kindOfMeasurementParameter(param)})` : name;
  const value = param ? formatMeasurementParameterValue(param) : '';
  return value ? `${label}: ${value}` : label;
}

function hasMeasurementParameterValue(
  param: TrackItMeasurementParameterDto | null | undefined,
): boolean {
  if (!param) return false;
  if (instanceOfTrackItMeasurementParameterAreaDto(param)) {
    return param.valueX != null || param.valueY != null;
  }
  return 'value' in param && param.value != null;
}

export function createMeasurementParameter(
  kind: TrackItMeasurementParameterKind,
  name: string,
  unit: string | undefined,
): TrackItMeasurementParameterDto {
  switch (kind) {
    case 'Area':
      return {
        id: NIL,
        name,
        unit,
        $type: Object.values(TrackItMeasurementParameterAreaDtoTypeEnum)[0],
      };
    case 'Boolean':
      return {
        id: NIL,
        name,
        $type: Object.values(TrackItMeasurementParameterBooleanDtoTypeEnum)[0],
      };
    case 'Double':
      return {
        id: NIL,
        name,
        unit,
        $type: Object.values(TrackItMeasurementParameterDoubleDtoTypeEnum)[0],
      };
    case 'Long':
      return {
        id: NIL,
        name,
        unit,
        $type: Object.values(TrackItMeasurementParameterLongDtoTypeEnum)[0],
      };
    case 'Modality':
      return {
        id: NIL,
        name,
        $type: Object.values(TrackItMeasurementParameterModalityDtoTypeEnum)[0],
      };
    case 'String':
      return {
        id: NIL,
        name,
        $type: Object.values(TrackItMeasurementParameterStringDtoTypeEnum)[0],
      };
  }
}

export function createParameterFilterEntry(
  name: string,
  measurementParameter: TrackItMeasurementParameterDto | undefined,
  isCustom: boolean,
): TrackItMeasurementParameterFilterDto {
  return {
    id: NIL,
    isEnabled: true,
    isCustom,
    measurementParameter: measurementParameter
      ? freshMeasurementParameter(measurementParameter)
      : createMeasurementParameter('String', name, undefined),
    $type: Object.values(TrackItMeasurementParameterFilterWithUserValueDtoTypeEnum)[0],
  };
}

export function trackItParameterIdentityKey(
  name: string,
  kind: TrackItMeasurementParameterKind,
  unit: string | undefined,
): string {
  return `parameter ${kind} ${name} ${unit ?? ''}`;
}

export function trackItPropertyIdentityKey(name: string): string {
  return `property ${name}`;
}

export function trackItFilterIdentityKey(
  filter: TrackItMeasurementFilterForCombinationDto,
): string {
  const name = trackItFilterName(filter) ?? '';
  if (!isParameterFilterEntry(filter)) return trackItPropertyIdentityKey(name);
  const param = filter.measurementParameter;
  const kind = param ? kindOfMeasurementParameter(param) : 'String';
  const unit = param ? unitOfMeasurementParameter(param) : undefined;
  return trackItParameterIdentityKey(name, kind, unit);
}

export function trackItFiltersMatch(
  a: TrackItMeasurementFilterForCombinationDto,
  b: TrackItMeasurementFilterForCombinationDto,
): boolean {
  return trackItFilterIdentityKey(a) === trackItFilterIdentityKey(b);
}

export function applyTrackItFilterSelection(
  combinations: ParameterCombinationDto[],
  selectedFilters: TrackItMeasurementParameterFilterDto[],
): ParameterCombinationDto[] {
  return combinations.map((combination) => {
    const existingFilters = combination.trackItFilters ?? [];
    const propertyFilters = existingFilters.filter(instanceOfTrackItMeasurementPropertyFilterDto);
    const kept = existingFilters.filter(
      (filter): filter is TrackItMeasurementParameterFilterDto => {
        if (instanceOfTrackItMeasurementPropertyFilterDto(filter)) return false;
        return selectedFilters.some((candidate) => trackItFiltersMatch(filter, candidate));
      },
    );
    const added = selectedFilters.filter(
      (candidate) => !kept.some((filter) => trackItFiltersMatch(filter, candidate)),
    );

    return { ...combination, trackItFilters: [...propertyFilters, ...kept, ...added] };
  });
}

export function createPropertyFilterEntry(
  name: string,
  isCustom: boolean,
): TrackItMeasurementPropertyFilterDto {
  const measurementProperty: TrackItMeasurementPropertyDto = {
    id: NIL,
    name,
    $type: Object.values(TrackItMeasurementPropertyDtoTypeEnum)[0],
  };

  return {
    id: NIL,
    isEnabled: true,
    isCustom,
    measurementProperty,
    $type: Object.values(TrackItMeasurementPropertyFilterDtoTypeEnum)[0],
  };
}

export function applyTrackItPropertyFilterSelection(
  combinations: ParameterCombinationDto[],
  selectedProperties: TrackItMeasurementPropertyFilterDto[],
): ParameterCombinationDto[] {
  return combinations.map((combination) => {
    const existingFilters = combination.trackItFilters ?? [];
    const conditionFilters = existingFilters.filter(
      (filter) => !instanceOfTrackItMeasurementPropertyFilterDto(filter),
    );
    const kept = existingFilters.filter(
      (filter): filter is TrackItMeasurementPropertyFilterDto =>
        instanceOfTrackItMeasurementPropertyFilterDto(filter) &&
        !!filter.measurementProperty?.name &&
        selectedProperties.some((prop) => trackItFiltersMatch(filter, prop)),
    );
    const added = selectedProperties.filter(
      (prop) => !kept.some((filter) => trackItFiltersMatch(filter, prop)),
    );

    return { ...combination, trackItFilters: [...conditionFilters, ...kept, ...added] };
  });
}

// Area's value lives in valueX/valueY instead of a single `value`.
export function freshMeasurementParameter(
  param: TrackItMeasurementParameterDto,
): TrackItMeasurementParameterDto {
  if (instanceOfTrackItMeasurementParameterAreaDto(param)) {
    return { ...param, id: NIL, trackItId: undefined, valueX: undefined, valueY: undefined };
  }
  return { ...param, id: NIL, trackItId: undefined, value: undefined };
}

function freshMeasurementProperty(
  property: TrackItMeasurementPropertyDto,
): TrackItMeasurementPropertyDto {
  return { ...property, id: NIL, trackItId: undefined, value: undefined };
}

function filterAsCatalogEntry(
  filter: TrackItMeasurementFilterForCombinationDto,
): TrackItMeasurementFilterForCombinationDto {
  if (instanceOfTrackItMeasurementPropertyFilterDto(filter)) {
    return {
      ...filter,
      id: NIL,
      measurementProperty: filter.measurementProperty
        ? freshMeasurementProperty(filter.measurementProperty)
        : filter.measurementProperty,
    };
  }
  return {
    ...filter,
    id: NIL,
    measurementParameter: filter.measurementParameter
      ? freshMeasurementParameter(filter.measurementParameter)
      : filter.measurementParameter,
  };
}

export function deriveTrackItFilterCatalog(
  combinations: ParameterCombinationDto[],
): TrackItMeasurementFilterForCombinationDto[] {
  const groups = new Map<string, TrackItMeasurementFilterForCombinationDto[]>();
  for (const combination of combinations) {
    for (const filter of combination.trackItFilters ?? []) {
      const key = trackItFilterIdentityKey(filter);
      const group = groups.get(key);
      if (group) group.push(filter);
      else groups.set(key, [filter]);
    }
  }
  return [...groups.values()].map((group) =>
    filterAsCatalogEntry(group.find((filter) => filter.isEnabled) ?? group[0]),
  );
}

export function deriveValueBearingCounts(
  combinations: ParameterCombinationDto[] | undefined | null,
  isProperty: boolean,
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const combination of combinations ?? []) {
    for (const filter of combination.trackItFilters ?? []) {
      const isPropertyFilter = instanceOfTrackItMeasurementPropertyFilterDto(filter);
      if (isPropertyFilter !== isProperty) continue;
      if (!trackItFilterName(filter)) continue;
      const hasValue = isPropertyFilter
        ? !!filter.measurementProperty?.value
        : !instanceOfTrackItMeasurementParameterFilterWithUserValueDto(filter) ||
          hasMeasurementParameterValue(filter.measurementParameter);
      if (hasValue) {
        const key = trackItFilterIdentityKey(filter);
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
  }
  return counts;
}

function collectSelectedTrackItFilters(
  combinations: ParameterCombinationDto[] | undefined | null,
  isProperty: boolean,
): TrackItMeasurementFilterForCombinationDto[] {
  return uniqBy(
    (combinations ?? [])
      .flatMap((combination) => combination.trackItFilters ?? [])
      .filter(
        (filter) =>
          instanceOfTrackItMeasurementPropertyFilterDto(filter) === isProperty &&
          trackItFilterName(filter),
      ),
    trackItFilterIdentityKey,
  );
}

// Overloaded so a literal isProperty argument narrows the return type instead of callers
// getting the parameter/property union back.
export function selectedTrackItFilters(
  combinations: ParameterCombinationDto[] | undefined | null,
  isProperty: false,
): TrackItMeasurementParameterFilterDto[];
export function selectedTrackItFilters(
  combinations: ParameterCombinationDto[] | undefined | null,
  isProperty: true,
): TrackItMeasurementPropertyFilterDto[];
export function selectedTrackItFilters(
  combinations: ParameterCombinationDto[] | undefined | null,
  isProperty: boolean,
): TrackItMeasurementFilterForCombinationDto[] {
  return collectSelectedTrackItFilters(combinations, isProperty);
}
